import React, { useState } from 'react'

import {
    Container, Typography, CssBaseline, Avatar,
    Grid, Box, Alert, Link, TextField, Button, CircularProgress
} from '@mui/material/';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { Copyright } from '../components/Copyright';

import { Link as RouterLink, Navigate } from "react-router-dom";
import { firebaseConfig } from "../firebase";

const theme = createTheme();

function getFunctionsRegion() {
    return firebaseConfig.projectId === "litport-heine-c2471" ? "europe-west3" : "europe-west1";
}

// Calls the server-side /register endpoint so the Firebase Auth user
// is never created in the browser – only after the backend has
// successfully set claims and queued the verification mail.
async function callRegisterEndpoint(payload) {
    const baseUrl = `https://${getFunctionsRegion()}-${firebaseConfig.projectId}.cloudfunctions.net`;

    const url = `${baseUrl}/app/register`;
    const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
    });
    const data = await response.json();
    if (!response.ok) {
        throw new Error(data.message || `HTTP ${response.status}`);
    }
    return data;
}


export default function Register() {
    const [error, setError] = useState("");
    const [successMessage, setSuccessMessage] = useState("");
    const [loading, setLoading] = useState(false);

    if (!firebaseConfig.registrationEnabled) {
        return <Navigate to="/login" replace />;
    }

    const handleSubmit = async (event) => {
        event.preventDefault();
        const data = new FormData(event.currentTarget);
        setError("");
        setSuccessMessage("");
        setLoading(true);

        try {
            const displayName = data.get("firstName") + " " + data.get("lastName");
            const email = data.get("email");
            const password = data.get("password");
            const accountid = data.get("accountid");
            const company = data.get("company");
            const salutation = data.get("salutation");
            const tenant = firebaseConfig.name;
            const freitext = data.get("freitext");

            await callRegisterEndpoint({
                displayName,
                email,
                password,
                accountid,
                company,
                tenant,
                salutation,
                freitext,
            });

            setSuccessMessage(
                "Registrierung erfolgreich. Bitte bestätigen Sie Ihre E-Mail-Adresse über den Link in der Verifizierungs-Mail. " +
                "Danach wird Ihr Konto von einem Litport Admin freigegeben."
            );
        } catch (err) {
            console.error("Registration error:", err);
            setError(err.message || "Registrierung fehlgeschlagen. Bitte versuchen Sie es später erneut.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <ThemeProvider theme={theme}>
            <Container component="main" maxWidth="xs">
                <CssBaseline />
                <Box
                    sx={{
                        marginTop: 8,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                    }}
                >
                    <Avatar sx={{ m: 1, bgcolor: 'primary.main' }}>
                        <LockOutlinedIcon />
                    </Avatar>

                    <Typography component="h1" variant="h5">
                        Im {firebaseConfig.name} Kundenportal Litport registrieren
                    </Typography>

                    {successMessage ? (
                        <Box sx={{ mt: 3, width: '100%' }}>
                            <Alert severity="success">{successMessage}</Alert>
                            <Box sx={{ mt: 2, textAlign: 'center' }}>
                                <Link component={RouterLink} to="/login" variant="body2">
                                    Zur Anmeldung
                                </Link>
                            </Box>
                        </Box>
                    ) : (
                    <Box component="form" noValidate onSubmit={handleSubmit} sx={{ mt: 3 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    required
                                    fullWidth
                                    id="tenant"
                                    label="Mandant"
                                    name="tenant"
                                    value={firebaseConfig.name}
                                    disabled
                                    autoComplete=""
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    required
                                    fullWidth
                                    id="company"
                                    label="Kunde"
                                    name="company"
                                    autoComplete=""
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    select
                                    fullWidth
                                    id="salutation"
                                    label="Anrede"
                                    name="salutation"
                                    SelectProps={{
                                        native: true,
                                    }}
                                >
                                    <option value=""></option>
                                    <option value="Herr">Herr</option>
                                    <option value="Frau">Frau</option>
                                    <option value="Divers">Divers</option>
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    autoComplete="given-name"
                                    name="firstName"
                                    required
                                    fullWidth
                                    id="firstName"
                                    label="Vorname"
                                    autoFocus
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    required
                                    fullWidth
                                    id="lastName"
                                    label="Nachname"
                                    name="lastName"
                                    autoComplete="family-name"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    required
                                    fullWidth
                                    id="email"
                                    label="Email Adresse"
                                    name="email"
                                    autoComplete="email"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    required
                                    fullWidth
                                    name="password"
                                    label="Password"
                                    type="password"
                                    id="password"
                                    autoComplete="new-password"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    required
                                    fullWidth
                                    id="accountid"
                                    label="Kundennummer (ggf. mehrere)"
                                    name="accountid"
                                    autoComplete=""
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    id="freitext"
                                    label="Anmerkungen"
                                    name="freitext"
                                    multiline
                                    rows={3}
                                    autoComplete=""
                                />
                            </Grid>



                            {/* <Grid item xs={12}>
                                <FormControlLabel
                                    control={<Checkbox value="allowExtraEmails" color="primary" />}
                                    label="I want to receive inspiration, marketing promotions and updates via email."
                                />
                            </Grid> */}
                        </Grid>
                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            disabled={loading}
                            sx={{ mt: 3, mb: 2 }}
                        >
                            {loading ? <CircularProgress size={24} color="inherit" /> : 'Registrieren'}
                        </Button>

                        {error && <Alert severity="error">{error}</Alert>}

                        <Grid container justifyContent="flex-end">
                            <Grid item xs={12} sx={{ mb: 2 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Mit der Registrierung stimmen Sie unserer{' '}
                                    <a 
                                        href={firebaseConfig.privacyLink} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        style={{ color: '#1976d2', textDecoration: 'underline' }}
                                    >
                                        Datenschutzerklärung
                                    </a>{' '}
                                    zu.
                                </Typography>
                            </Grid>
                            <Grid item>
                                <Link component={RouterLink} to="/" variant="body2">
                                    Haben Sie schon ein Account? Anmelden
                                </Link>
                            </Grid>
                        </Grid>

                    </Box>
                    )}
                </Box>
                <Copyright sx={{ mt: 5 }} />
            </Container>
        </ThemeProvider>
    );
}